/**
 * Aiguillage de l'application.
 *
 * Trois états seulement, tant que rien d'autre n'a été conçu :
 *   - on regarde s'il reste une session valable (au rechargement de la page) ;
 *   - sinon, l'écran de connexion ;
 *   - une fois entré, la barre haute et l'écran du module courant.
 *
 * **La barre haute est ici, et non dans les écrans**, depuis le 03/09/2026.
 * Elle l'était dans `EcranAnnuaire`, ce qui tenait tant qu'il n'y avait qu'un
 * écran : le deuxième l'aurait recopiée, et deux barres jumelles auraient
 * divergé au premier changement porté d'un seul côté — la raison même qui a
 * fait paramétrer l'annuaire plutôt que le dédoubler. Chaque écran ne rend plus
 * que son corps.
 *
 * **Le sous-menu appartient au module, pas à la barre.** C'est ce fichier qui
 * sait que le module Annuaire a trois entrées et que ce sont les trois
 * annuaires ouverts ; la barre ne fait que dessiner la liste qu'on lui donne.
 * Un module sans sous-menu n'en dessine pas.
 *
 * Pas de routeur ni d'URL : le module courant et l'annuaire courant sont des
 * états de React, décidé le 03/09/2026 — même raisonnement que pour le reste de
 * l'écran. Conséquence assumée et à reprendre : un rechargement ramène aux
 * clients, et une fiche ne se partage pas par un lien.
 *
 * Le rechargement repasse par `/moi` : un jeton encore valide ne prouve pas que
 * l'accès l'est toujours. Un compte désactivé perd la main au rechargement, sans
 * attendre l'expiration du jeton.
 */

import { useCallback, useEffect, useState } from 'react';
import { lireMoi, raisonDeSortie, SORTIES, type Moi } from './api.js';
import { authentification, jetonCourant, seDeconnecter } from './authentification.js';
import { BarreHaute } from './BarreHaute.js';
import { EcranAnnuaire } from './EcranAnnuaire.js';
import { EcranConnexion } from './EcranConnexion.js';
import { EcranAbsences } from './EcranAbsences.js';
import { EcranAffectation } from './EcranAffectation.js';
import { EcranParc } from './EcranParc.js';
import { EcranOrdres } from './EcranOrdres.js';
import { lancerLaLecture } from './a_creer.js';
import { EcranPlanning } from './EcranPlanning.js';
import { EcranAffretement } from './EcranAffretement.js';
import { EcranAccueil, type Cible } from './EcranAccueil.js';
import { EcranLitiges } from './EcranLitiges.js';
import { EcranComptabilite } from './EcranComptabilite.js';
import { EcranFinance } from './EcranFinance.js';
import { EcranTarifs } from './EcranTarifs.js';
import { EcranEmballage } from './EcranEmballage.js';
import { EcranPilotage } from './EcranPilotage.js';
import { EcranParametres } from './EcranParametres.js';
import { EcranEcheancier } from './EcranEcheancier.js';
import { EcranFactures } from './EcranFactures.js';
import { EcranPrevisions } from './EcranPrevisions.js';
import { EcranBfr } from './EcranBfr.js';
import { EcranTresorerie } from './EcranTresorerie.js';
import { EcranResultatAnalyse } from './EcranResultatAnalyse.js';
import { EcranRentabilite } from './EcranRentabilite.js';
import { EcranIndicateurs } from './EcranIndicateurs.js';
import { PanneauSourcesPennylane } from './PanneauSourcesPennylane.js';
import { messageRefusAnalyse } from './refusAnalyse.js';
import { ANNUAIRE_CLIENTS } from './clients.js';
import { ANNUAIRE_TRANSPORTEURS } from './transporteurs.js';
import { ANNUAIRE_SITES } from './sites.js';
import { capitale, type Annuaire } from './annuaire.js';
import { MODULES, ecransDe, type SousMenu } from './modules.js';
import { NavigationTelephone } from './NavigationTelephone.js';
import { PARCS } from './parcs.js';

/** Les annuaires ouverts, dans l'ordre du sous-menu. */
const ANNUAIRES: readonly Annuaire[] = [
  ANNUAIRE_CLIENTS,
  ANNUAIRE_TRANSPORTEURS,
  ANNUAIRE_SITES,
];

/**
 * LE GROUPE ANNUAIRE, dans Exploitation depuis le lot 95 (17/09/2026) — il
 * était un module. Un annuaire, une entrée, déduite de la liste ci-dessus
 * plutôt qu'écrite à côté : sinon ouvrir un quatrième annuaire demanderait de
 * penser à deux endroits.
 */
const GROUPE_ANNUAIRE: SousMenu = {
  cle: 'annuaire',
  nom: 'Annuaire',
  enfants: ANNUAIRES.map((a) => ({ cle: a.chemin, nom: capitale(a.pluriel) })),
};

/**
 * LE GROUPE RESSOURCES, dans Exploitation depuis le lot 95 — il était un
 * module. L'ordre est celui de la maquette validée le 03/09 : l'affectation
 * d'abord — c'est ce qu'on regarde tous les matins —, puis les absences, puis
 * les trois parcs, déduits de `PARCS` pour la même raison que ci-dessus.
 */
const GROUPE_RESSOURCES: SousMenu = {
  cle: 'ressources',
  nom: 'Ressources',
  enfants: [
    { cle: 'affectation', nom: 'Affectation' },
    { cle: 'absences', nom: 'Absences' },
    ...PARCS.map((p) => ({ cle: p.cle, nom: capitale(p.pluriel) })),
  ],
};

/**
 * LE GROUPE ANALYSE, dans Finance depuis le lot 97 (18/09/2026). Décision du
 * 17/09 (« Analyse financière : 8. les arbitrages ») : un groupe « Analyse ▾ »
 * comme Annuaire et Ressources dans Exploitation, Indicateurs en premier.
 *
 * L'ORDRE EST CELUI DU CADRAGE, mais seuls les écrans construits y figurent :
 * une porte qui ne mène nulle part ne vaut pas mieux que rien. Les lots
 * suivants ajoutent les leurs à leur place.
 */
const GROUPE_ANALYSE: SousMenu = {
  cle: 'analyse',
  nom: 'Analyse',
  enfants: [
    // L'ORDRE DU CADRAGE (doc 8) : Indicateurs en premier, puis BFR,
    // Échéancier, Prévisions, Résultat, Rentabilité, Trésorerie.
    { cle: 'indicateurs', nom: 'Indicateurs' },
    { cle: 'bfr', nom: 'BFR' },
    { cle: 'echeancier', nom: 'Échéancier' },
    // AJOUTÉ AU LOT 106 : la liste de toutes les pièces, pour remonter d'un
    // total à ses lignes quand un chiffre paraît faux.
    { cle: 'factures', nom: 'Factures' },
    { cle: 'previsions', nom: 'Prévisions' },
    { cle: 'resultat', nom: 'Résultat' },
    { cle: 'rentabilite', nom: 'Rentabilité' },
    { cle: 'tresorerie', nom: 'Trésorerie' },
  ],
};

/**
 * Le sous-menu du module FINANCE — deux entrées, et pas cinq.
 *
 * Demande de l'utilisateur du 09/09/2026 : « on va mettre les 3 menus
 * existants en sous-menu d'un menu s'appelant Prix de revient. On va ajouter
 * le menu Comptabilité avec les sous-menus suivants : Facture, Recouvrement ».
 *
 * Le cadrage de l'étape 10 en prévoyait cinq (Facturation, Recouvrement,
 * Analyse, Ressources, Paramètres) ; on n'écrit que ce qui existe. « Analyse »
 * n'a pas encore de place décidée, et une porte qui ne mène nulle part ne vaut
 * pas mieux que rien.
 */
/**
 * L'ORDRE A ÉTÉ INVERSÉ le 09/09/2026, sur constat de l'utilisateur : « Prix de
 * revient dans le module finance doit être le dernier dans les menus car c'est
 * celui qu'on utilise le moins ». Le prix de revient se règle une fois par
 * trimestre ; la comptabilité s'ouvre tous les jours.
 */
const SOUS_MENU_FINANCE: readonly SousMenu[] = [
  { cle: 'comptabilite', nom: 'Comptabilité' },
  // LES TARIFS, 15/09/2026. Entre la comptabilité et le prix de revient parce
  // que c'est leur place dans le raisonnement : ce qu'on a convenu de vendre,
  // ce qu'on a facturé, ce que ça nous a coûté.
  { cle: 'tarifs', nom: 'Tarifs' },
  // LE RÉSULTAT DES JOURNÉES, posé ici le 17/09/2026, A ÉTÉ RETIRÉ DU MENU au
  // lot 95 à la demande de Mohamed. Décision : « juste retirer l'onglet ».
  // L'écran `EcranResultat` et le calcul de l'API restent en place, sans accès
  // depuis le menu ; où il reviendra — Exploitation › Journées était l'idée du
  // matin — reste à décider.
  { cle: 'prix-de-revient', nom: 'Prix de revient' },
  GROUPE_ANALYSE,
];

/**
 * Le sous-menu du module Exploitation, dans l'ordre de la maquette validée.
 *
 * Le PLANNING ouvre le 04/09/2026, juste après les ordres — c'est l'écran qu'on
 * ouvre le matin en arrivant. Les entrées suivantes du cadrage (recherche de
 * place, optimisation, litiges) ne sont pas écrites tant qu'elles
 * n'existent pas : une porte qui ne mène nulle part ne vaut pas mieux que rien.
 */
const SOUS_MENU_EXPLOITATION: readonly SousMenu[] = [
  { cle: 'ordres', nom: 'Ordres de transport' },
  { cle: 'planning', nom: 'Planning' },
  // LES JOURNÉES prennent la place de « Préparation » le 14/09/2026 : le
  // bouclage de ce qui a roulé, camion par camion. Elles se placent APRÈS le
  // planning — on planifie d'abord, on pointe ensuite.
  // RENOMMÉES « PILOTAGE » le 02/11/2026 (lot 148) : un tableau de bord et le
  // pointage, en deux onglets. La clé reste `journees` : elle ne se voit pas,
  // et la changer toucherait chaque lien interne vers cet écran.
  { cle: 'journees', nom: 'Pilotage' },
  // LA PRÉPARATION A ÉTÉ RETIRÉE LE 12/09/2026, à la demande de l'utilisateur :
  // « on n'a jamais utilisé le menu tournée ». Ouverte le 05/09, elle n'a servi
  // à personne — aucune tournée n'a été créée. Une porte qui ne mène nulle part
  // ne vaut pas mieux que rien, et c'est déjà la règle de ce menu.
  //
  // CE QUI RESTE, ET POURQUOI : la TABLE `tournees` et les routes de l'API
  // demeurent. Le planning lit `etapes.tournee_id` (planning.ts 168-175) et le
  // coût d'un ordre prend sa part du péage de la tournée (imputation.ts
  // 362-367). Surtout, le BOUCLAGE DES TOURNÉES qui remplace cet écran s'en
  // servira probablement — `tournees` et la `journees_vehicule` prévue à
  // l'étape 15 posent la même question au même objet. À trancher au cadrage du
  // bouclage, pas avant.
  // LES LITIGES ouvrent le 07/09/2026, entre la préparation et l'emballage :
  // c'est l'ordre du cadrage §6, « Ordres de transport, Planification,
  // Litiges, Emballage ». Ils se SAISISSENT sur l'étape, dans la fiche de
  // l'ordre ; cette entrée est la liste de travail — ce qu'il reste à régler,
  // et ce que ça bloque en facturation.
  { cle: 'litiges', nom: 'Litiges' },
  // L'EMBALLAGE ouvre le 06/09/2026. Il ne saisit rien : l'échange se pose sur
  // l'ÉTAPE, dans la fiche de l'ordre — le cadrage §6 est explicite. Cette
  // entrée MONTRE les soldes, qui se calculent des mouvements, et TIENT les
  // bons que les clients nous remettent.
  { cle: 'emballage', nom: 'Emballage' },
  // L'AFFRÈTEMENT ouvre le 06/09/2026, DANS Exploitation — décision du 05/09 :
  // « on enlève le module affrètement, tout est intégré à exploitation ». Il
  // répond à une seule question : qui appeler pour cet axe, avec ce matériel.
  { cle: 'affretement', nom: 'Affrètement' },
  // L'ANNUAIRE ET LES RESSOURCES, lot 95 : deux groupes qui ouvrent un petit
  // menu, après les écrans du transport (variante B de la maquette).
  GROUPE_ANNUAIRE,
  GROUPE_RESSOURCES,
];

/**
 * TOUTES LES CLÉS D'ÉCRAN D'EXPLOITATION, groupes dépliés. Elles doivent être
 * uniques : `exploitation` est un seul état pour les quatorze écrans. Vérifié
 * au chargement du fichier plutôt que supposé — deux écrans de même clé
 * ouvriraient toujours le premier, sans que rien le dise.
 */
const CLES_EXPLOITATION = ecransDe(SOUS_MENU_EXPLOITATION).map((e) => e.cle);
if (new Set(CLES_EXPLOITATION).size !== CLES_EXPLOITATION.length) {
  throw new Error(
    `Exploitation : clé d'écran en double (${CLES_EXPLOITATION.join(', ')})`,
  );
}

// LA MÊME RÈGLE POUR FINANCE depuis le lot 97 : le groupe Analyse ajoute ses
// écrans au même état `finance` que la comptabilité et les tarifs.
const CLES_FINANCE = ecransDe(SOUS_MENU_FINANCE).map((e) => e.cle);
if (new Set(CLES_FINANCE).size !== CLES_FINANCE.length) {
  throw new Error(`Finance : clé d'écran en double (${CLES_FINANCE.join(', ')})`);
}

type Etat =
  | { readonly nom: 'verification' }
  | { readonly nom: 'dehors'; readonly sortie?: string | null; readonly codeSeulement?: boolean }
  | { readonly nom: 'dedans'; readonly moi: Moi };

export function Application() {
  const [etat, setEtat] = useState<Etat>({ nom: 'verification' });
  // ON ARRIVE SUR L'ACCUEIL depuis le 07/09/2026. Avant, c'était l'Annuaire —
  // non par choix mais parce que l'accueil n'existait pas.
  const [module, setModule] = useState('accueil');
  // UN SEUL ÉTAT POUR LES QUATORZE ÉCRANS D'EXPLOITATION depuis le lot 95 :
  // l'annuaire et les ressources avaient chacun le leur quand ils étaient des
  // modules. Leurs clés sont distinctes — c'est vérifié plus haut.
  const [exploitation, setExploitation] = useState('ordres');
  // Le module Finance porte deux familles : la comptabilité (ce qu'on facture)
  // et le prix de revient (ce que ça coûte). Elles ne partagent aucun écran,
  // d'où deux branches et non un onglet de plus dans Finance.
  //
  // FINANCE S'OUVRE SUR LA COMPTABILITÉ depuis le 09/09/2026, en même temps que
  // l'inversion du menu : ouvrir sur l'écran qu'on a mis en dernier PARCE QU'ON
  // s'en sert le moins se contredirait tout seul.
  const [finance, setFinance] = useState('comptabilite');
  /**
   * Un ordre que le PLANNING demande d'ouvrir. C'est le seul passage d'un écran
   * à l'autre du logiciel, et il tient en un identifiant : l'application
   * bascule sur les ordres de transport, l'écran des ordres l'ouvre, puis
   * remet ceci à `null` pour qu'un retour à la liste ne le rouvre pas.
   */
  const [ordreAOuvrir, setOrdreAOuvrir] = useState<string | null>(null);
  /**
   * D'OÙ L'ON VENAIT QUAND ON A OUVERT CET ORDRE — 14/09/2026.
   *
   * Demande de Mohamed : « en cas de retour on revient dans la page à facturer
   * et pas sur la liste de tous les OT ». Le défaut valait pour les QUATRE
   * écrans qui savent ouvrir un ordre : le Planning, les Litiges,
   * l'Affrètement — et désormais « À facturer ».
   *
   * `null` = on est arrivé par le menu Exploitation. Le retour ramène à la
   * liste des ordres, et le bouton dit « Les OT ».
   *
   * IL N'EST PAS REMIS À `null` PAR `quandOrdreOuvert`, contrairement à
   * `ordreAOuvrir`. Les deux ne vivent pas le même temps : l'identifiant est
   * consommé DÈS L'OUVERTURE, sans quoi un retour à la liste rouvrirait la
   * fiche ; le chemin du retour doit survivre jusqu'à ce qu'on l'emprunte.
   * C'est le bouton de retour lui-même qui le remet à `null`.
   */
  const [retourOrdre, setRetourOrdre] = useState<{
    readonly libelle: string;
    readonly quand: () => void;
  } | null>(null);

  /**
   * OUVRIR UN ORDRE DEPUIS UN AUTRE ÉCRAN, en retenant le chemin du retour.
   *
   * Écrite une seule fois : les cinq appelants passaient les mêmes deux
   * lignes, et il en manquait une chez chacun. Le libellé est celui de
   * l'onglet ou du menu d'où l'on part — c'est ce que la personne lit à
   * l'écran au moment où elle clique.
   */
  const ouvrirUnOrdre = useCallback(
    (otId: string, libelle: string, revenir: () => void) => {
      setOrdreAOuvrir(otId);
      setRetourOrdre({
        libelle,
        quand: () => {
          setRetourOrdre(null);
          revenir();
        },
      });
      setModule('exploitation');
      setExploitation('ordres');
    },
    [],
  );
  /**
   * L'onglet IMPORT de l'écran Paramètres, demandé depuis l'accueil.
   *
   * L'import n'est pas une entrée de sous-menu : c'est un ONGLET dans
   * `EcranParametres` (voir le commentaire de son `importOuvert`). L'accueil
   * doit pourtant pouvoir y mener directement, sans quoi son action
   * « + Import de données » déposerait la personne sur la liste des supports —
   * un lien qui ne tient pas ce qu'il annonce.
   *
   * REMIS À FAUX dès qu'on va ailleurs, y compris sur Paramètres par la barre
   * haute : sans cela, ouvrir Paramètres par le menu rouvrirait l'import une
   * fois sur deux, sans que personne comprenne pourquoi.
   */
  const [parametresSurImport, setParametresSurImport] = useState(false);

  // Ces deux fonctions descendent dans l'écran des clients, où elles servent de
  // dépendances à un `useEffect`. Sans `useCallback`, elles changeraient
  // d'identité à chaque rendu et l'effet se relancerait sans fin.
  const sortir = useCallback(() => {
    void (async () => {
      // LOT 170 : une session coupée par l'API (24 h, autre poste) se ferme
      // SUR CE POSTE SEULEMENT, et la connexion dit pourquoi.
      const raison = raisonDeSortie();
      if (raison === 'code_requis') {
        setEtat({ nom: 'dehors', codeSeulement: true });
        return;
      }
      await seDeconnecter(raison === null ? 'global' : 'local');
      setEtat({ nom: 'dehors', sortie: raison === null ? null : SORTIES[raison] });
    })();
  }, []);

  const jeton = useCallback(() => jetonCourant(), []);

  useEffect(() => {
    let abandonne = false;

    void (async () => {
      const { data } = await authentification.getSession();
      const session = data.session;
      if (session === null) {
        if (!abandonne) setEtat({ nom: 'dehors' });
        return;
      }
      const acces = await lireMoi(session.access_token);
      if (abandonne) return;
      if (acces.etat === 'ok') {
        setEtat({ nom: 'dedans', moi: acces.moi });
      } else if (acces.cause === 'code_requis') {
        // LOT 170 : la session existe, il ne manque que le code.
        if (!abandonne) setEtat({ nom: 'dehors', codeSeulement: true });
      } else if (acces.cause === 'session_expiree' || acces.cause === 'session_remplacee') {
        await seDeconnecter('local');
        if (!abandonne) setEtat({ nom: 'dehors', sortie: SORTIES[acces.cause] });
      } else {
        // La session existe encore mais l'accès ne suit plus : on la referme
        // plutôt que de la laisser retrouvée au prochain chargement.
        await seDeconnecter();
        if (!abandonne) setEtat({ nom: 'dehors' });
      }
    })();

    return () => {
      abandonne = true;
    };
  }, []);

  /**
   * LA LECTURE DES CONFIRMATIONS DÉPOSÉES — lot 147, 02/11/2026. Lancée dès
   * qu'on est connecté, quel que soit l'écran : « travailler à côté ». Celles
   * déposées avant une fermeture de Stafa sont lues ici, à la réouverture.
   */
  const connecte = etat.nom === 'dedans';
  useEffect(() => {
    if (connecte) lancerLaLecture(jeton);
  }, [connecte, jeton]);

  if (etat.nom === 'verification') {
    return (
      <main className="scene">
        <p className="attente">Vérification de la session…</p>
      </main>
    );
  }

  if (etat.nom === 'dehors') {
    return (
      <EcranConnexion
        quandConnecte={(moi) => setEtat({ nom: 'dedans', moi })}
        sortie={etat.sortie ?? null}
        codeSeulement={etat.codeSeulement === true}
      />
    );
  }

  /**
   * ALLER QUELQUE PART, depuis l'accueil.
   *
   * Chaque module range son sous-menu dans SON état, et c'est voulu : deux
   * modules ne partagent pas une entrée courante. L'aiguillage se fait donc
   * ici, sur le module visé. Depuis le lot 95, l'annuaire et les ressources
   * sont des écrans d'Exploitation : l'accueil les vise par ce module.
   *
   * Une version antérieure posait le sous-menu reçu dans `exploitation` QUEL
   * QUE SOIT le module visé. Aller aux clients depuis l'accueil écrivait donc
   * « clients » dans l'état de l'exploitation : l'annuaire s'ouvrait sur la
   * mauvaise entrée, et le module Exploitation gardait une clé qui ne désigne
   * aucun de ses écrans.
   */
  const aller = ({ module: cible, sousMenu }: Cible) => {
    setParametresSurImport(cible === 'parametres' && sousMenu === 'import');

    if (cible === 'exploitation' && sousMenu !== undefined) {
      // « nouvel-ordre » n'est PAS une entrée du sous-menu : c'est le geste
      // « créer », qui vit dans l'écran des ordres. On y va, et l'écran
      // s'ouvre sur sa liste — le bouton de création y est.
      const vise = sousMenu === 'nouvel-ordre' ? 'ordres' : sousMenu;
      // Une clé inconnue laisse l'écran qui était ouvert, plutôt que d'ouvrir
      // les ordres par défaut sans rien dire.
      if (CLES_EXPLOITATION.includes(vise)) setExploitation(vise);
    }

    setModule(cible);
  };

  const societe = etat.moi.societes[0];
  /** L'annuaire ou le parc désigné par l'écran courant d'Exploitation, s'il y en a un. */
  const annuaireOuvert: Annuaire | undefined = ANNUAIRES.find(
    (a) => a.chemin === exploitation,
  );
  const parcOuvert = PARCS.find((p) => p.cle === exploitation);

  /**
   * LE TITRE DE LA PAGE — refonte graphique, maquettes validées le 31/10/2026.
   * Chaque planche porte en tête le nom de l'écran (« Journées », « Litiges »,
   * « Comptabilité »…). Il est pris du menu, une seule fois ici, plutôt que
   * réécrit dans chaque écran : il ne peut pas diverger de l'entrée cliquée.
   *
   * PAS DE TITRE SUR LES ORDRES : la fiche porte déjà le sien (« Nouvel ordre
   * de transport », « Ordre de transport n° … »). NI SUR L'ACCUEIL, dont la
   * planche ouvre sur la phrase du jour.
   */
  const titrePage = ((): string | null => {
    if (module === 'accueil') return null;
    if (module === 'parametres') return 'Paramètres';
    if (module === 'exploitation') {
      // Le PLANNING porte déjà le sien, la semaine affichée, dans sa barre.
      if (exploitation === 'ordres' || exploitation === 'planning') return null;
      return ecransDe(SOUS_MENU_EXPLOITATION).find((e) => e.cle === exploitation)?.nom ?? null;
    }
    if (module === 'finance') {
      return ecransDe(SOUS_MENU_FINANCE).find((e) => e.cle === finance)?.nom ?? null;
    }
    return null;
  })();

  return (
    <div className="application">
      <BarreHaute
        modules={MODULES}
        module={module}
        quandModule={(cle) => {
          setParametresSurImport(false);
          setModule(cle);
        }}
        sousMenu={
          module === 'exploitation'
            ? SOUS_MENU_EXPLOITATION
            : module === 'finance'
              ? SOUS_MENU_FINANCE
              : // AUCUN SOUS-MENU AILLEURS, ET C'EST LE DÉFAUT.
                //
                // Il l'est depuis le 07/09/2026, sur constat de l'utilisateur :
                // le module FINANCE affichait le sous-menu des Ressources —
                // Affectation, Absences, les trois parcs — qui n'y mènent à
                // rien. En cause, un défaut par REPLI : tout module non nommé
                // ci-dessus recevait celui des ressources. Un module qui
                // s'ouvre sans qu'on y pense héritait donc du menu d'un autre.
                //
                // PARAMÈTRES tombe ici aussi, et c'est voulu : ses trois
                // listes sont des ONGLETS dans l'écran. Elles se lisent
                // d'affilée, et une barre de plus au-dessus d'un tableau de
                // quatre lignes ferait deux niveaux pour rien.
                //
                // FINANCE aura les siens — le cadrage de l'étape 10 en prévoit
                // cinq : Facturation, Recouvrement, Analyse, Ressources,
                // Paramètres. Tant qu'un seul écran existe, il n'y a rien à
                // choisir, et une barre à une entrée ne dit rien.
                []
        }
        sousMenuCourant={module === 'finance' ? finance : exploitation}
        quandSousMenu={(cle) => {
          if (module === 'finance') {
            setFinance(cle);
            return;
          }
          setExploitation(cle);
        }}
        societe={societe?.raison_sociale ?? 'Société inconnue'}
        utilisateur={etat.moi.utilisateur}
        quandDeconnexion={sortir}
      />
      {titrePage !== null && <h1 className="titre-page">{titrePage}</h1>}
      {module === 'accueil' ? (
        <EcranAccueil
          jeton={jeton}
          nomUtilisateur={etat.moi.utilisateur.nom}
          quandAccesPerdu={sortir}
          quandAller={aller}
        />
      ) : module === 'finance' ? (
        finance === 'comptabilite' ? (
          <EcranComptabilite
            jeton={jeton}
            quandAccesPerdu={sortir}
            /* LE RETOUR DIT « À FACTURER » et ramène ici, sur l'onglet d'où
               l'on vient — demande du 14/09/2026. */
            quandOuvrirOrdre={(otId) => {
              ouvrirUnOrdre(otId, 'À facturer', () => {
                setModule('finance');
                setFinance('comptabilite');
              });
            }}
          />
        ) : finance === 'tarifs' ? (
          <EcranTarifs jeton={jeton} quandAccesPerdu={sortir} />
        ) : finance === 'previsions' ? (
          <EcranPrevisions
            jeton={jeton}
            quandAccesPerdu={sortir}
            messageRefus={messageRefusAnalyse}
          />
        ) : finance === 'factures' ? (
          <EcranFactures
            jeton={jeton}
            quandAccesPerdu={sortir}
            messageRefus={messageRefusAnalyse}
            sources={(quandLu) => (
              <PanneauSourcesPennylane
                jeton={jeton}
                quandAccesPerdu={sortir}
                messageRefus={messageRefusAnalyse}
                quandLu={quandLu}
              />
            )}
          />
        ) : finance === 'indicateurs' ? (
          <EcranIndicateurs
            jeton={jeton}
            quandAccesPerdu={sortir}
            messageRefus={messageRefusAnalyse}
            sources={(quandLu) => (
              <PanneauSourcesPennylane
                jeton={jeton}
                quandAccesPerdu={sortir}
                messageRefus={messageRefusAnalyse}
                quandLu={quandLu}
              />
            )}
          />
        ) : finance === 'rentabilite' ? (
          <EcranRentabilite
            jeton={jeton}
            quandAccesPerdu={sortir}
            messageRefus={messageRefusAnalyse}
            sources={(quandLu) => (
              <PanneauSourcesPennylane
                jeton={jeton}
                quandAccesPerdu={sortir}
                messageRefus={messageRefusAnalyse}
                quandLu={quandLu}
              />
            )}
          />
        ) : finance === 'resultat' ? (
          <EcranResultatAnalyse
            jeton={jeton}
            quandAccesPerdu={sortir}
            messageRefus={messageRefusAnalyse}
            sources={(quandLu) => (
              <PanneauSourcesPennylane
                jeton={jeton}
                quandAccesPerdu={sortir}
                messageRefus={messageRefusAnalyse}
                quandLu={quandLu}
              />
            )}
          />
        ) : finance === 'tresorerie' ? (
          <EcranTresorerie
            jeton={jeton}
            quandAccesPerdu={sortir}
            messageRefus={messageRefusAnalyse}
            sources={(quandLu) => (
              <PanneauSourcesPennylane
                jeton={jeton}
                quandAccesPerdu={sortir}
                messageRefus={messageRefusAnalyse}
                quandLu={quandLu}
              />
            )}
          />
        ) : finance === 'bfr' ? (
          <EcranBfr
            jeton={jeton}
            quandAccesPerdu={sortir}
            messageRefus={messageRefusAnalyse}
            sources={(quandLu) => (
              <PanneauSourcesPennylane
                jeton={jeton}
                quandAccesPerdu={sortir}
                messageRefus={messageRefusAnalyse}
                quandLu={quandLu}
              />
            )}
          />
        ) : finance === 'echeancier' ? (
          <EcranEcheancier
            jeton={jeton}
            quandAccesPerdu={sortir}
            messageRefus={messageRefusAnalyse}
            sources={(quandLu) => (
              <PanneauSourcesPennylane
                jeton={jeton}
                quandAccesPerdu={sortir}
                messageRefus={messageRefusAnalyse}
                quandLu={quandLu}
              />
            )}
          />
        ) : (
          <EcranFinance jeton={jeton} quandAccesPerdu={sortir} />
        )
      ) : module === 'parametres' ? (
        <EcranParametres
          jeton={jeton}
          quandAccesPerdu={sortir}
          ouvrirImport={parametresSurImport}
        />
      ) : // EXPLOITATION — le seul module restant. Depuis le lot 95, ses écrans
      // comprennent l'annuaire et les ressources.
      annuaireOuvert !== undefined ? (
        <EcranAnnuaire
          annuaire={annuaireOuvert}
          jeton={jeton}
          quandAccesPerdu={sortir}
        />
      ) : exploitation === 'affectation' ? (
        <EcranAffectation jeton={jeton} quandAccesPerdu={sortir} />
      ) : exploitation === 'absences' ? (
        <EcranAbsences jeton={jeton} quandAccesPerdu={sortir} />
      ) : parcOuvert !== undefined ? (
        <EcranParc parc={parcOuvert} jeton={jeton} quandAccesPerdu={sortir} />
      ) : exploitation === 'litiges' ? (
        <EcranLitiges
          jeton={jeton}
          quandAccesPerdu={sortir}
          quandOuvrirOrdre={(otId) => {
            ouvrirUnOrdre(otId, 'Litiges', () => setExploitation('litiges'));
          }}
        />
      ) : exploitation === 'affretement' ? (
        <EcranAffretement
          jeton={jeton}
          quandAccesPerdu={sortir}
          quandOuvrirOrdre={(otId) => {
            ouvrirUnOrdre(otId, 'Affrètement', () => setExploitation('affretement'));
          }}
        />
      ) : exploitation === 'emballage' ? (
        <EcranEmballage jeton={jeton} quandAccesPerdu={sortir} />
      ) : exploitation === 'journees' ? (
        <EcranPilotage jeton={jeton} quandAccesPerdu={sortir} />
      ) : exploitation === 'planning' ? (
        <EcranPlanning
          jeton={jeton}
          quandAccesPerdu={sortir}
          quandOuvrirOrdre={(otId) => {
            ouvrirUnOrdre(otId, 'Planning', () => setExploitation('planning'));
          }}
        />
      ) : (
        <EcranOrdres
          jeton={jeton}
          quandAccesPerdu={sortir}
          ouvrirOrdre={ordreAOuvrir}
          quandOrdreOuvert={() => setOrdreAOuvrir(null)}
          retourVers={retourOrdre}
        />
      )}
      {/* LA NAVIGATION SUR TÉLÉPHONE — lot 133. Toujours rendue, montrée par la
          feuille de style sous 640 px seulement. Elle lit les MÊMES sous-menus
          que la barre haute. */}
      <NavigationTelephone
        module={module}
        exploitation={exploitation}
        finance={finance}
        sousMenuExploitation={SOUS_MENU_EXPLOITATION}
        sousMenuFinance={SOUS_MENU_FINANCE}
        quandAller={(cible, ecran) => {
          setParametresSurImport(false);
          if (cible === 'exploitation' && ecran !== undefined) setExploitation(ecran);
          if (cible === 'finance' && ecran !== undefined) setFinance(ecran);
          setModule(cible);
        }}
        societe={societe?.raison_sociale ?? 'Société inconnue'}
        utilisateur={etat.moi.utilisateur}
        quandDeconnexion={sortir}
      />
    </div>
  );
}
